import React, { useState, useEffect, useLayoutEffect } from 'react';
import { useTheme, Fab, Stack, Box, IconButton, Typography, Grid, Modal, Button, Container, CircularProgress, Tooltip } from '@mui/material';
import { DataGridPro, DataGridProProps, GridColDef, GridRenderCellParams, GridToolbar, GridInitialState, useGridApiRef, gridRowCountSelector } from '@mui/x-data-grid-pro';
import { Add as AddIcon, Cached as CachedIcon, Edit as EditIcon, Delete as DeleteIcon } from '@mui/icons-material';
// import { useAmaWorkflowContext } from '../../../../context/ama-workflow-provider';
import { useAmaV2Context } from 'context/ama-v2-provider';
import { useDynamicGridHeight } from '../../../../hooks/useDynamicGridHeight';
import ClientsListRowTemplate from '../clients-list-row-template';
import { useSearchParams } from 'react-router-dom';
import { EditMode } from '../../../../data/enums';
import { localStorageKeys, urlParams } from '../../../../data/constants';
import { AmaV2Client } from 'context/ama-v2-provider/ama-v2.types';

const GRID_VERT_OFFSET = 300;

interface IClientsListProps {
  mode: EditMode;
  setEditMode: React.Dispatch<React.SetStateAction<EditMode>>;
};

export default function ClientsList(props: IClientsListProps) {
  
  const { clientsState, loadClients, setSelectedClient, deleteClient, clearClientsError } = useAmaV2Context();
  const theme = useTheme();
  const apiRef = useGridApiRef();
  const [params, setParams] = useSearchParams();  
  // const getDetailPanelContent = React.useCallback<NonNullable<DataGridProProps['getDetailPanelContent']>>(({ row }) => <DetailPanelContent row={row} />, []);
  // const getDetailPanelHeight = React.useCallback(() => 'auto', []);
  // Detail panel temporarily disabled; expansion toggle removed.
  
  const [idForDelete, setIdForDelete] = useState<string | null>(null);
  const { gridHeight } = useDynamicGridHeight({ gridItems: clientsState.data, offset: GRID_VERT_OFFSET });

  // useLayoutEffect(() => {

  //   // Save grid state when view unmounts
  //   return () => {
  //     if (apiRef.current && apiRef.current.exportState) {
  //       console.log(`ClientsList.useLayoutEffect :: saving grid state to local storage`);
  //       localStorage.setItem(
  //         localStorageKeys.AMA_CLIENTS_GRID_STATE, 
  //         JSON.stringify(apiRef.current.exportState()));
  //     }
  //   };
  // }, []);

  // useLayoutEffect(() => {

  //   const selectedId = params.get(urlParams.ID);
  //   if (!selectedId || !clientsState.data || !apiRef.current) return;

  //   // Working around DataGridPro bug related to scrollToIndexes
  //   setTimeout(() => {
  //     try {

  //       console.log(`ClientsList :: scrolling to selected item`);
  //       const rowIndex = clientsState.data.findIndex(client => client.client_id === selectedId);
  //       if (rowIndex >= 0) apiRef.current?.scrollToIndexes({ rowIndex: rowIndex, colIndex: 0 }); 
      
  //     } catch (error) {
  //       console.error(`ClientsList :: error scrolling to selected item: ${error}`);
  //     }
  //   }, 500);

  // }, [clientsState.data]);

  const getInitialGridState = () : GridInitialState => {
    
    // Check for saved grid filters
    const gridStateStr = localStorage.getItem(localStorageKeys.AMA_CLIENTS_GRID_STATE);
    
    // Init initial grid state obj
    const gridState = gridStateStr ? JSON.parse(gridStateStr) : {};

    // Check querystring for selected row and update grid state
    const selectedId = params.get(urlParams.ID);
    if (selectedId) {
      const detailPanel = { 'expandedRowIds': [selectedId] };
      gridState.detailPanel = detailPanel;
    }

    // TODO - Check querystring for grid filter and update grid state
    const qsFilterStr = params.get(urlParams.AMA_GRID_FILTER);   

    // Return initial grid state
    return gridState;
  };

  const handleEditButtonClick = (id: string) => {
    setSelectedClient(id);   
    props.setEditMode(EditMode.Edit);
  };

  const handleDeleteButtonClick = async (id: string) => {
    setIdForDelete(id);
  };

  const handleAddButtonClick = () => {
    setSelectedClient(null);
    props.setEditMode(EditMode.Add);
  };

  const handleRefreshButtonClick = async () => {
    await loadClients();
  };

  const handleModalOkButtonClick = async () => {
    if (idForDelete) {
      const id = idForDelete;
      setIdForDelete(null);
      await deleteClient(id);
    }
  };

  const handleModalCancelButtonClick = () => {
    setIdForDelete(null);
  };

  const handleErrorModalOkButtonClick = () => {
    clearClientsError();
  };

  const CustomFooter = () => {

    return (
      <Container sx={{ height:  40 }}>
        <Grid container justifyContent="space-between" alignItems="center" sx={{ height: '100%', color: theme.palette.text.secondary }}>
          <Grid item xs={4}>
            <Typography variant="body2" align="left">
              Records: {clientsState.data?.length}
            </Typography>
          </Grid>
          <Grid item xs={4} sx={{ display: 'flex', justifyContent: 'center' }}>
            <Typography variant="body2" align="center" sx={{ display: 'flex', alignItems: 'center', color: theme.palette.text.secondary }}>
            <a href="#"
              aria-label="Refresh Client List"
              aria-describedby='client_refresh_timestamp'
              title='Refresh Client List'
              onClick={(e) => { e.preventDefault(); handleRefreshButtonClick();}}>
             <CachedIcon 
                sx={{ 
                  verticalAlign: 'middle', 
                  mr: 1, mb:.3, 
                  color: theme.palette.text.disabled,
                  animation: clientsState.status === 'loading' ? 'spin 1s linear infinite' : 'none', 
                }} />            
            </a>
            <span id="client_refresh_timestamp"><span className='visually-hidden'>Last Refreshed: </span>{clientsState.lastUpdated}</span>
            </Typography>
          </Grid>
          <Grid item xs={4} md={4}>
            &nbsp;
          </Grid>
        </Grid>
      </Container>
    );
  };

  // const DetailPanelContent = ({ row }: { row: AmaV2Client }) => {
  //   return (
  //     <ClientsListRowTemplate 
  //       client={row} />
  //   );
  // };

  const RenderEditColumn = (client: AmaV2Client) => {
    if (!client) return <></>
    return (
      <Tooltip title="Edit client record.">
        <IconButton 
          aria-label="Edit client record."
          sx={{ color: theme.palette.grey[600]}}
          onClick={() => 
            { 
              console.log(`ClientsList.RenderEditColumn :: edit click for client_id=${client.client_id}`);
              handleEditButtonClick(client.client_id);
            }}>
          <EditIcon />
        </IconButton>
      </Tooltip>
    )
  };
    
  const RenderDeleteColumn = (client: AmaV2Client) => {
    if (!client) return <></>
    const isHideAdminUI = process.env.REACT_APP_HIDE_ADMIN_UI === 'true';
    return (
      <Tooltip 
        title="Delete client record."
        {...(isHideAdminUI ? { className: 'admin-ui' } : {})}>
        <IconButton 
          aria-label="Delete client record."
          sx={{ color: theme.palette.grey[600]}}
          onClick={() => 
            { 
              console.log(`ClientsList.RenderDeleteColumn :: delete click for client_id=${client.client_id}`);
              handleDeleteButtonClick(client.client_id)
            }}>
          <DeleteIcon />
        </IconButton>
      </Tooltip>
    )
  };

  const columns: Array<GridColDef> = [
    { 
      field: 'display_name', type: 'string', 
      headerName: 'Client Name', 
      flex: .4,
      resizable: true, sortable: true
    },
    { 
      field: 'client_id', type: 'string', 
      headerName: 'Client ID', 
      flex: .2, minWidth: 75,
      resizable: true, sortable: true
    },
    { 
      field: 'zip_5', type: 'string', 
      headerName: 'ZIP Code', 
      flex: .2, minWidth: 75,
      resizable: true, sortable: true
    },
    {
      field: 'pbi_partition_status', type: 'string',
      headerName: 'Partition',
      flex: .2, minWidth: 100,
      resizable: true, sortable: true,
      valueGetter: (params) => {
        const partition = params?.row?.pbi_partition_status;
        return (typeof partition === 'string' && partition.trim().length > 0) ? partition : '--';
      }
    },
    { 
      field: 'notes', type: 'string', 
      headerName: 'Notes', 
      flex: .4,
      resizable: true, sortable: true
    },
    { 
      field: 'Delete', type: 'string', 
      headerName: process.env.REACT_APP_HIDE_ADMIN_UI === 'true' ? '' : 'Delete', 
      width: 75, 
      headerAlign: 'center', align: 'center',
      resizable: true, sortable: false,
      renderCell: (params: GridRenderCellParams<any, Date>) => (<> { RenderDeleteColumn(params.row as AmaV2Client) } </>)      
    },    
    { 
      field: 'Edit', type: 'string', 
      headerName: 'Edit', 
      width: 75,  
      headerAlign: 'center', align: 'center',
      resizable: true, sortable: false,
      renderCell: (params: GridRenderCellParams<any, Date>) => (<> { RenderEditColumn(params.row as AmaV2Client) } </>)
    }
  ];

  return (
    <Box sx={{ width: '100%', height: '100%' }}>
          
      {/* Loading overlay */}
      {clientsState.status === 'loading' && (
        <div
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            background: 'rgba(255, 255, 255, 0.6)',
            zIndex: 999,
          }}>
          <CircularProgress />
        </div>
      )}

      {/* Grid */}
      {clientsState.data &&

        <DataGridPro
          columns={columns}
          rows={wrapClients(clientsState.data)}
          rowThreshold={0}
          isRowSelectable={() => false}
          // getDetailPanelHeight={getDetailPanelHeight} // disabled
          // getDetailPanelContent={getDetailPanelContent} // disabled
          apiRef={apiRef}
          initialState={getInitialGridState()}
          disableDensitySelector={true}
          disableColumnResize={false}
          disableColumnSelector={true}
          slots={{ toolbar: GridToolbar, footer: () => (
            <Stack justifyContent="center" alignItems="center">
              <CustomFooter />
            </Stack>
          ) }}
          slotProps={{
            toolbar: {
              showQuickFilter: true,
            
              printOptions: { disableToolbarButton: true },
              csvOptions: {disableToolbarButton: true}                
          }}}
          getRowClassName={(params) =>
            params.indexRelativeToCurrentPage % 2 === 0 ? 'Mui-even' : 'Mui-odd'
          }            
          sx={{
            height: gridHeight,
            minHeight: '300px',
            '& .MuiDataGrid-root': {
              color: theme => theme.palette.text.primary,
              fontSize: '0.9rem',
              maxHeight: '100%'
            },
            '& .MuiDataGrid-columnHeader': {
              backgroundColor: theme => theme.palette.background.paper,
              color: theme => theme.palette.text.primary,
              fontSize: '.9rem'
            },
            '& .MuiDataGrid-cell': {
              color: theme => theme.palette.text.primary,
              fontSize: '0.9rem'
            },
            '& .MuiDataGrid-row.Mui-even .MuiDataGrid-cell': {
              backgroundColor: theme => theme.palette.grey[100]
            },
            '& .MuiDataGrid-row:hover .MuiDataGrid-cell': {
              backgroundColor: theme => theme.palette.grey[300]
            }
          }}
        />

        //disableColumnMenu={true}
        //disableColumnFilter={true}
        
      }
      
      {/* Add Client button */}
      <Fab color="primary" aria-label="add"        
        sx={{ position: "absolute", bottom: '10px', right: '65px' }} onClick={() => handleAddButtonClick()}>
        <AddIcon />
      </Fab>

      {/* Delete confirmation modal */}
      <Modal
        open={Boolean(idForDelete)}
        aria-labelledby="modal-modal-title"
        aria-describedby="modal-modal-description">
        <Box sx={{
          position: 'absolute' as const,
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          width: 400,
          bgcolor: 'background.paper',
          border: `1px solid ${theme.palette.grey[300]}`,
          boxShadow: 24,
          p: 4,}}>
          <Typography id="modal-modal-title" variant="h6" component="h2">
            Delete
          </Typography>
          <Typography id="modal-modal-description" sx={{ mt: 2, mb: 4 }}>
            Are you sure you want to delete {idForDelete}?
          </Typography>
          <Box sx={{ display: 'flex', justifyContent: 'center' }}>
            <Button variant="contained" color="primary" sx={{mr: 2}} onClick={handleModalOkButtonClick}>Ok</Button>
            <Button variant="outlined" color="primary" onClick={handleModalCancelButtonClick}>Cancel</Button>
          </Box>
        </Box>
      </Modal>

      {/* Error modal */}
      <Modal
        open={Boolean(clientsState.error)}
        aria-labelledby="client-error-modal-title"
        aria-describedby="client-error-modal-description">
        <Box sx={{
          position: 'absolute' as const,
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          width: 400,
          bgcolor: 'background.paper',
          border: `1px solid ${theme.palette.grey[300]}`,
          boxShadow: 24,
          p: 4,}}>
          <Typography id="client-error-modal-title" variant="h6" component="h2">
            Error
          </Typography>
          <Typography id="client-error-modal-description" sx={{ mt: 2, mb: 4 }}>
            {clientsState.error}
          </Typography>
          <Box sx={{ display: 'flex', justifyContent: 'center' }}>
            <Button variant="contained" color="primary" onClick={handleErrorModalOkButtonClick}>Ok</Button>
          </Box>
        </Box>
      </Modal>

    </Box>
  );
}

const wrapClients = (clients: Array<AmaV2Client>) => {  
  return clients.map((jsonObject) => {
    const newObj = { ...jsonObject, id: jsonObject.client_id };
    return newObj;
  });
};	

